/**
 * The four-step driver progress strip.
 *
 * It reflects `driver_status.state` as the dispatch system already models it
 * (src/Utils/getDriverStatusLabel.js). The strip itself only displays; the
 * advance button under it (TripDetail) posts the next event to
 * `/portal/trip-event` and the strip re-renders from the trip the API returns.
 */
import React from 'react';
import { PROGRESS_STEPS } from '../portalFormat';

/**
 * `steps` / `label` name the steps and the bar: the driver's Hungarian by
 * default, the operator screens' English (operatorFormat) when passed.
 *
 * `compact` drops the row of step names and leaves the bars, for a place
 * that shows one strip per row rather than one per screen — the office's
 * Daily Overview timeline, where four names under every trip would cost
 * more height than the whole indicator is worth. The steps themselves are
 * unchanged: same count, same order, same `aria-valuemax`, and the reader
 * still gets `label` and the current value. What names the step there is a
 * single line of text beside the strip, so the words stay the ones the
 * driver and the dispatcher see.
 */
export default function ProgressStrip({
  index = 0, steps = PROGRESS_STEPS, label = 'A fuvar állapota', compact = false,
}) {
  return (
    <div className={`pp-progress${compact ? ' pp-progress--compact' : ''}`}>
      <div
        className="pp-progress__bars"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={index}
        aria-label={label}
      >
        {steps.map((step, i) => (
          <span
            key={step}
            className={`pp-progress__bar${i < index ? ' pp-progress__bar--on' : ''}`}
          />
        ))}
      </div>
      {compact ? null : (
        <div className="pp-progress__labels">
          {steps.map((step, i) => (
            <span key={step} className={i === index - 1 ? 'pp-progress__label--on' : undefined}>
              {step}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
